import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Typography from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import parse from "html-react-parser";
import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => {
    return {state}
};
const enum fields {
    targetDate = "targetDate",
    title = "title",
    titleFontSize = "titleFontSize",
    backgroundColor1 = "backgroundColor1",
    backgroundColor2 = "backgroundColor2",
    subTitle = "subTitle",
    subTitleFontSize = "subTitleFontSize",
    footerTitle = "footerTitle",
    footerSubtitle = "footerSubtitle",
    image = "image",
    background = "background",
    logoType = "logoType",
    logoImage = "logoImage",
    logoImageWidth = "logoImageWidth",
    dateFontSize = "dateFontSize",
    dateLabelFontSize = "dateLabelFontSize",
    timerEndText = "timerEndText",
}
type Props = WidgetProps & ReturnType<typeof mapStateToProps>;
interface PropsComponent extends Props {
    fields: {
        [fields.targetDate]: string;
        [fields.title]: string;
        [fields.titleFontSize]: string;
        [fields.subTitleFontSize]: string;
        [fields.backgroundColor1]: string;
        [fields.backgroundColor2]: string;
        [fields.subTitle]: string;
        [fields.footerTitle]: string;
        [fields.footerSubtitle]: string;
        [fields.image]: string;
        [fields.background]: "image" | "color";
        [fields.logoType]: "text" | "image";
        [fields.logoImage]: string;
        [fields.logoImageWidth]: string;
        [fields.dateFontSize]: string;
        [fields.dateLabelFontSize]: string;
        [fields.timerEndText]: string;
    };
}

interface CountDownStyles {
    countdownWrapper?: InjectableCss;
    countNumber?: InjectableCss;
    countLabel?: InjectableCss;
    title?: InjectableCss;
    subTitle?: InjectableCss;
    footerTitle?: InjectableCss;
    footerSubtitle?: InjectableCss;
}

export const CountDownStyles: CountDownStyles = {
    countdownWrapper: {
        css: css`
            span{
                text-transform: uppercase;
            }
            margin-top: 30px;
            display: flex;
            flex-direction: row;
            justify-content: center;
            align-items: center;
            font-size: 1.5rem;
            font-weight: bold;
            margin-bottom: 30px;
            font-weight: 400;
            >div{
                padding: 0px 25px;
                display: flex;
                flex-direction: column;
                align-items: center;
                gap: 0.5rem;
                border-right: 2px solid #FFFFFF;
            }
            > div:first-child {
                border-left: 2px solid #FFFFFF;
            }
            ${({ theme }: { theme: BaseTheme }) =>
            breakpointMediaQueries(
                theme,
                [
                    null,
                    css`
                        >div{
                            padding: 0px 18px !important;
                        }
                    `,
                    null,
                    null,
                    null
                ],"max",
            )}
        `,
    },
    countNumber: {
        css: css`
            color: #FFFFFF;
            font-size: 5rem;
            font-weight: bold;
            line-height: 75px;
            ${({ theme }: { theme: BaseTheme }) =>
            breakpointMediaQueries(
                theme,
                [
                    null,
                    css`
                        font-size: 3rem !important;
                        line-height: 40px !important;
                    `,
                    null,
                    null,
                    null
                ],"max",
            )}
        `,
    },
    countLabel: {
        css: css`
            color: #FFFFFF;
            font-size: 1rem;
            font-weight: 300;
            text-transform: uppercase;
            ${({ theme }: { theme: BaseTheme }) =>
            breakpointMediaQueries(
                theme,
                [
                    null,
                    css`
                        font-size: 0.7rem !important;
                    `,
                    null,
                    null,
                    null
                ],"max",
            )}
        `,
    },
    title: {
        css: css`
            color: #FFFFFF;
            font-size: 2.2rem;
            font-weight: bold;
            text-transform: uppercase;
            letter-spacing: 1px;
            ${({ theme }: { theme: BaseTheme }) =>
            breakpointMediaQueries(
                theme,
                [
                    null,
                    css`
                        font-size: 1.3rem !important;
                    `,
                    null,
                    null,
                    null
                ],"max",
            )}
        `,
    },
    subTitle: {
        css: css`
            color: #FFFFFF;
            font-size: 1.2rem;
            font-weight: bold;
            text-transform: uppercase;
            letter-spacing: 1px;
            ${({ theme }: { theme: BaseTheme }) =>
            breakpointMediaQueries(
                theme,
                [
                    null,
                    css`
                        font-size: 0.7rem !important;
                    `,
                    null,
                    null,
                    null
                ],"max",
            )}
        `,
    },
    footerTitle: {
        css: css`
            color: white;
            font-size: 3rem;
            font-family: fantasy;
            text-transform: uppercase;
        `,
    },
    footerSubtitle: {
        css: css`
            color: white;
            font-size: 0.8rem;
            text-transform: uppercase;
            letter-spacing: 2px;
            ${({ theme }: { theme: BaseTheme }) =>
            breakpointMediaQueries(
                theme,
                [
                    null,
                    css`
                        font-size: 0.7rem !important;
                    `,
                    null,
                    null,
                    null
                ],"max",
            )}
        `,
    },
};

const styles = CountDownStyles;

const CountdownBannerWidget: React.FC<PropsComponent> = ({
    fields: {
        [fields.targetDate]: targetDate,
        [fields.backgroundColor1]: backgroundColor1 = "#204873",
        [fields.backgroundColor2]: backgroundColor2 = "#021738 ",
        [fields.title]: title = "Countdown",
        [fields.subTitle]: sub = "Countdown",
        [fields.footerTitle]: footerTitle = "Countdown",
        [fields.footerSubtitle]: footerSubtitle = "Countdown",
        [fields.image]: image = "",
        [fields.background]: background = "color",
        [fields.logoType]: logoType = "text",
        [fields.logoImage]: logoImage = "",
        [fields.logoImageWidth]: logoImageWidth = "100",
        [fields.dateFontSize]: dateFontSize = "5",
        [fields.dateLabelFontSize]: dateLabelFontSize = "1",
        [fields.titleFontSize]: titleFontSize = "2.2",
        [fields.subTitleFontSize]: subTitleFontSize = "1.2",
        [fields.timerEndText]: timerEndText = "Time's up!",
    },
}) => {
   
    const getBrazilTargetTime = (brazilDateTime: string | Date) => {
        if (typeof brazilDateTime === "string") {
        const isoWithOffset = `${brazilDateTime.replace(" ", "T")  }-03:00`;
        return new Date(isoWithOffset);
        }
        return new Date(brazilDateTime);
    };

    const getTargetTime = () => {
        const date = getBrazilTargetTime(typeof targetDate === "string" ? new Date(targetDate) : targetDate);
        return isNaN(date.getTime()) ? new Date() : date;
    };

    const [days, setDays] = useState("00");
    const [hours, setHours] = useState("00");
    const [minutes, setMinutes] = useState("00");
    const [seconds, setSeconds] = useState("00");
    const [timeReached, setTimeReached] = useState(false);
    const targetTime = getTargetTime();
    const isValidDate = targetTime instanceof Date && !isNaN(targetTime.getTime());

    let countdownInterval:any= null;

    function startCountdown() {
        clearInterval(countdownInterval);
        const date = new Date(targetTime);
        const pad = (n:any) => n.toString().padStart(2, "0");
        const year = date.getFullYear();
        const month = pad(date.getMonth() + 1); // Months are 0-indexed
        const day = pad(date.getDate());
        const hours = pad(date.getHours());
        const minutes = pad(date.getMinutes());

        const formatted = `${year}-${month}-${day}T${hours}:${minutes}`;
        const input = formatted;

        if (!input) {
        console.log("Please select a date and time.");
        return;
        }

        // User input assumed to be in BRT (UTC−03:00)
        const target = new Date(`${input  }:00-03:00`);

        countdownInterval = setInterval(() => {
        const now = new Date();
        const diff = target.getTime() - now.getTime();

        if (diff <= 0) {
          clearInterval(countdownInterval);
          setTimeReached(true);
          return;
        }

        const days = Math.floor(diff / (1000 * 60 * 60 * 24));
        const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
        const seconds = Math.floor((diff % (1000 * 60)) / 1000);
        setDays(pad(days));
        setHours(pad(hours));
        setMinutes(pad(minutes));
        setSeconds(pad(seconds));

      }, 1000);
    }

    useEffect(() => {
        startCountdown();
    }, []);

const backgroundStyle = background === "color"
    ? {
        background: `radial-gradient(circle, ${backgroundColor1} 25%, ${backgroundColor2} 80%)`
    }
    : background === "image"
    ? {
        backgroundImage: `url(${image})`,
        backgroundSize: "cover",
        backgroundRepeat: "no-repeat",
        backgroundPosition: "center"
    }
    : {};
    return (
        <StyledWrapper
            style={{...backgroundStyle,
                padding: "2rem 1rem",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                minWidth: "250px",
                textAlign: "center",
                boxShadow: "0 2px 8px rgba(0,0,0,0.07)",
            }}
        >
                {title && <Typography {...styles.title} style={{ ...(styles.countNumber || {}),  ...(titleFontSize ? { fontSize: `${titleFontSize}rem` } : {}) }}>{translate(title)}</Typography>}
                {sub && <Typography {...styles.subTitle} style={{ ...(styles.countNumber || {}),  ...(subTitleFontSize ? { fontSize: `${subTitleFontSize}rem` } : {}) }}>{translate(sub)}</Typography>}

                {!isValidDate ? (
                    <Typography>{siteMessage("Invalid target date")}</Typography>
                ) : (
                    <StyledWrapper {...styles.countdownWrapper}>
                        {timeReached ? <Typography {...styles.countNumber} style={{ ...(styles.countNumber || {}),  ...(dateFontSize ? { fontSize: `3rem` } : {}) }}>{translate(timerEndText)}</Typography>
                            :
                            <>
                                <StyledWrapper>
                                    <Typography {...styles.countNumber} style={{ ...(styles.countNumber || {}),  ...(dateFontSize ? { fontSize: `${dateFontSize}rem` } : {}) }}>{days}</Typography> 
                                    <Typography {...styles.countLabel} style={{ ...(styles.countLabel || {}),  ...(dateLabelFontSize ? { fontSize: `${dateLabelFontSize}rem` } : {}) }}>{translate("DAYS")}</Typography>
                                </StyledWrapper>
                                <StyledWrapper>
                                    <Typography {...styles.countNumber} style={{ ...(styles.countNumber || {}),  ...(dateFontSize ? { fontSize: `${dateFontSize}rem` } : {}) }}>{hours}</Typography> 
                                    <Typography {...styles.countLabel} style={{ ...(styles.countLabel || {}),  ...(dateLabelFontSize ? { fontSize: `${dateLabelFontSize}rem` } : {}) }}>{translate("HOURS")}</Typography>
                                </StyledWrapper>
                                <StyledWrapper>
                                    <Typography {...styles.countNumber} style={{ ...(styles.countNumber || {}),  ...(dateFontSize ? { fontSize: `${dateFontSize}rem` } : {}) }}>{minutes}</Typography> 
                                    <Typography {...styles.countLabel} style={{ ...(styles.countLabel || {}),  ...(dateLabelFontSize ? { fontSize: `${dateLabelFontSize}rem` } : {}) }}>{translate("MINUTES")}</Typography>
                                </StyledWrapper>
                            </>
                        }
                    </StyledWrapper>
                )}
                {logoType === "image" && logoImage ? (
                    <img src={logoImage} alt="Logo" style={{ maxWidth: `${logoImageWidth}px`, marginBottom: "1rem" }} />
                ) : footerTitle ? <Typography {...styles.footerTitle}>{translate(footerTitle)}</Typography>:null
                }
                
                {footerSubtitle && <Typography {...styles.footerSubtitle}>
                     {parse(footerSubtitle, parserOptions)}
                </Typography>}
        </StyledWrapper>
    );
};

// Widget module definition
const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(CountdownBannerWidget),
    definition: {
        group: "Basic",
        icon: "text",
        displayName: "Countdown widget",
        fieldDefinitions: [
            {
                name: "targetDate",
                displayName: "Target Date",
                defaultValue: null,
                editorTemplate: "DateTimeField",
                fieldType: "Translatable",
                isRequired: true,
            },
            {
                name: "dateFontSize",
                displayName: "Date Font Size",
                defaultValue: "5",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                isRequired: true,
            },
            {
                name: "dateLabelFontSize",
                displayName: "Date Label Font Size",
                defaultValue: "1",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                isRequired: true,
            },
            {
                name: "background",
                displayName: "Background",
                editorTemplate: "DropDownField",
                fieldType: "Translatable",
                options: [
                    { displayName: "Image", value: "image" },
                    { displayName: "Color", value: "color" },
                ],
                defaultValue: "color",
                hideEmptyOption: true,
            },
            {
                name: "backgroundColor1",
                displayName: "Center background Color",
                editorTemplate: "ColorPickerField",
                defaultValue: "#204873",
                fieldType: "Translatable",
                isVisible: widget => widget.fields.background === "color",
            },
            {
                name: "backgroundColor2",
                displayName: "Left right background Color",
                editorTemplate: "ColorPickerField",
                defaultValue: "#021738",
                fieldType: "Translatable",
                isVisible: widget => widget.fields.background === "color",
            },
            {
                name: "image",
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                fieldType: "Translatable",
                isVisible: widget => widget.fields.background === "image",
            },
            {
                name: "title",
                displayName: "Title",
                editorTemplate: "TextField",
                defaultValue: "Countdown",
                fieldType: "Translatable",
            },
            {
                name: "titleFontSize",
                displayName: "Title Font Size",
                editorTemplate: "TextField",
                defaultValue: "2.2",
                fieldType: "Translatable",
                isRequired: true,
            },
            {
                name: "subTitle",
                displayName: "Sub Title",
                editorTemplate: "TextField",
                defaultValue: "Countdown",
                fieldType: "Translatable",
            },
            {
                name: "subTitleFontSize",
                displayName: "Sub Title Font Size",
                editorTemplate: "TextField",
                defaultValue: "1.2",
                fieldType: "Translatable",
                isRequired: true,
            },
            {
                name: "logoType",
                displayName: "Logo Type",
                editorTemplate: "DropDownField",
                fieldType: "Translatable",
                options: [
                    { displayName: "Image", value: "image" },
                    { displayName: "Text", value: "text" },
                ],
                defaultValue: "text",
                hideEmptyOption: true,
            },
            {
                name: "footerTitle",
                displayName: "Logo Title",
                editorTemplate: "TextField",
                defaultValue: "Countdown",
                fieldType: "Translatable",
                isVisible: widget => widget.fields.logoType === "text",
            },
            {
                name: "logoImage",
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                fieldType: "Translatable",
                isVisible: widget => widget.fields.logoType === "image",
            },
            {
                name: "logoImageWidth",
                displayName: "Logo Image Width",
                editorTemplate: "TextField",
                defaultValue: "100",
                fieldType: "Translatable",
                isVisible: widget => widget.fields.logoType === "image",
            },
            {
                name: "footerSubtitle",
                displayName: "Footer Sub title",
                editorTemplate: "RichTextField",
                defaultValue: "Countdown",
                fieldType: "Translatable",
            },
            {
                name: "timerEndText",
                displayName: "Timer End Text",
                editorTemplate: "TextField",
                defaultValue: "Time's up!",
                fieldType: "Translatable",
                isRequired: true,
            },
        ],
    },
};

export default widgetModule;